import React from 'react'
import { useTranslate } from 'src/localization/Trns'

import type { ReportingChartRendererProps } from './reportingChartPlan'
import { compileReportingChartPlan } from './reportingChartPlan'
import {
  ChartCaption,
  ChartFrame,
  ChartSurface,
  ChartValue,
} from './ReportingChartRenderer.style'
import { formatReportingValue, ReportingVisxChart } from './ReportingVisxChart'

const ReportingChartRenderer: React.FC<ReportingChartRendererProps> = ({
  chartSpec,
  dataset,
  presentation = 'card',
  fitToContainer = false,
  showFallbackCaption = true,
}) => {
  const translate = useTranslate()
  const isEmbedded = presentation === 'embedded'
  const plan = compileReportingChartPlan(chartSpec, dataset)
  if (!plan) return null

  const ariaLabel = chartSpec.title || translate('Report visualization')
  const caption =
    chartSpec.description ??
    (showFallbackCaption ? translate('Exact report data') : undefined)
  if (plan.kind === 'kpi') {
    return (
      <ChartFrame
        $embedded={isEmbedded}
        $fitToContainer={fitToContainer}
        data-testid="reporting-chart"
        data-chart-kind={plan.kind}
        role="img"
        aria-label={ariaLabel}
      >
        <ChartValue>
          {formatReportingValue(
            plan.data[0]?.value,
            plan.valueFormat,
            plan.valueCurrency,
          )}
        </ChartValue>
        {caption !== undefined && <ChartCaption>{caption}</ChartCaption>}
      </ChartFrame>
    )
  }

  return (
    <ChartFrame
      $embedded={isEmbedded}
      $fitToContainer={fitToContainer}
      data-testid="reporting-chart"
      data-chart-kind={plan.kind}
      data-chart-renderer="visx"
      role="group"
      aria-label={ariaLabel}
    >
      <ChartSurface $fitToContainer={fitToContainer}>
        <ReportingVisxChart plan={plan} />
      </ChartSurface>
      {caption !== undefined && <ChartCaption>{caption}</ChartCaption>}
    </ChartFrame>
  )
}

export type {
  ReportingChartPlan,
  ReportingChartRendererProps,
  ReportingCurrencyDisplay,
  ReportingRenderableChartKind,
} from './reportingChartPlan'
export {
  canRenderReportingChart,
  compileReportingChartPlan,
} from './reportingChartPlan'
export { ReportingChartRenderer }
export default ReportingChartRenderer
